/**
 * ArtifactGallery — grid view of all artifacts across all conversations,
 * with a share filter (ADR-0013): All / Yours / Shared with you. "Shared
 * with you" lists other members' live org share links (each opens its
 * /share/{token} viewer); your own artifacts carry a "Shared" chip when a
 * live link exists.
 */

import { useEffect, useMemo, useState } from "react";
import { apiFetch, isNativeHost } from "@/api/client";
import { openArtifactSmart, revealArtifact } from "@/api/files";
import { listExposure, listShares, revokeShare, type SharedItem } from "@/api/shares";
import { parseUTC } from "@/api/types";
import { useToastStore } from "@/components/common/Toast";

interface ArtifactItem {
  id: string;
  filename: string;
  file_type: string;
  conversation_id: string;
  conversation_title: string;
  created_at: string;
  updated_at: string;
}

const FILE_ICONS: Record<string, string> = {
  xlsx: "📊",
  csv: "📄",
  json: "📋",
  pdf: "📕",
  html: "🌐",
  htm: "🌐",
  md: "📄",
  txt: "📄",
};

type Tab = "all" | "yours" | "shared" | "byyou" | "exposure";

function formatDate(ts: string): string {
  return parseUTC(ts).toLocaleDateString([], {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function ArtifactGallery() {
  const [artifacts, setArtifacts] = useState<ArtifactItem[]>([]);
  const [shares, setShares] = useState<SharedItem[]>([]);
  // null = viewer is not an admin (the fetch 403s) — the tab hides itself.
  const [exposure, setExposure] = useState<SharedItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<Tab>("all");

  useEffect(() => {
    (async () => {
      try {
        const [arts, shr, exp] = await Promise.all([
          apiFetch<{ artifacts: ArtifactItem[] }>("/shortlists/artifacts"),
          listShares().catch(() => ({ shares: [] as SharedItem[] })),
          listExposure().catch(() => null),
        ]);
        setArtifacts(arts.artifacts);
        setShares(shr.shares);
        setExposure(exp ? exp.shares : null);
      } catch (e) {
        console.error("Failed to load artifacts:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addToast = useToastStore((s) => s.add);

  // Artifact ids YOU have a live link for → "Shared" chip on your cards.
  const myShared = useMemo(
    () => new Set(shares.filter((s) => s.mine && s.artifact_id).map((s) => s.artifact_id)),
    [shares],
  );
  const sharedWithYou = useMemo(() => shares.filter((s) => !s.mine), [shares]);
  const sharedByYou = useMemo(() => shares.filter((s) => s.mine), [shares]);

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(window.location.origin + url);
      addToast("Link copied.", "success");
    } catch {
      addToast("Couldn't access the clipboard — copy the link manually.", "error");
    }
  };

  const revoke = async (token: string) => {
    try {
      await revokeShare(token);
      setShares((prev) => prev.filter((s) => s.token !== token));
      setExposure((prev) => (prev ? prev.filter((s) => s.token !== token) : prev));
      addToast("Share link revoked.", "success");
    } catch (e) {
      addToast((e as Error).message || "Couldn't revoke the link.", "error");
    }
  };

  const q = search.trim().toLowerCase();
  const filteredArtifacts = q
    ? artifacts.filter((a) =>
        a.filename.toLowerCase().includes(q) ||
        a.conversation_title?.toLowerCase().includes(q),
      )
    : artifacts;
  const filteredShares = q
    ? sharedWithYou.filter((s) =>
        s.filename.toLowerCase().includes(q) || s.shared_by.toLowerCase().includes(q),
      )
    : sharedWithYou;
  const filteredByYou = q
    ? sharedByYou.filter((s) => s.filename.toLowerCase().includes(q))
    : sharedByYou;

  const filteredExposure = q
    ? (exposure ?? []).filter((s) =>
        s.filename.toLowerCase().includes(q) || s.shared_by.toLowerCase().includes(q),
      )
    : exposure ?? [];

  const showYours = tab === "all" || tab === "yours";
  const showShared = tab === "all" || tab === "shared";
  const showByYou = tab === "byyou";
  const showExposure = tab === "exposure";
  const visibleCount =
    (showYours ? filteredArtifacts.length : 0) +
    (showShared ? filteredShares.length : 0) +
    (showByYou ? filteredByYou.length : 0) +
    (showExposure ? filteredExposure.length : 0);
  const totalCount = artifacts.length + sharedWithYou.length;

  // "Folder" (reveal in file manager) only works in the native desktop shell;
  // hide it in a browser (cloud web), where Open already serves/downloads.
  // The share tabs are the inverse: links are cloud-only, so with no shares
  // visible anywhere (desktop) the tab row hides itself.
  const showReveal = isNativeHost();
  const showTabs = shares.length > 0 || !isNativeHost();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-space-40">Loading artifacts...</div>
    );
  }

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: "all", label: "All" },
    { key: "yours", label: "Yours" },
    { key: "shared", label: "Shared with you", count: sharedWithYou.length },
    { key: "byyou", label: "Shared by you", count: sharedByYou.length },
    // Admin-only governance view: every live PUBLIC link org-wide.
    ...(exposure !== null
      ? [{ key: "exposure" as Tab, label: "Public links (org)", count: exposure.length }]
      : []),
  ];

  return (
    <div className="h-full overflow-y-auto scrollbar-on-hover p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header — stacks on phones so the fixed-width search doesn't
            collide with the title. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-space mb-0.5">All Artifacts</h2>
            <p className="text-sm text-space-50">
              {totalCount} file{totalCount !== 1 ? "s" : ""} across all conversations
            </p>
          </div>

          {totalCount > 3 && (
            <div className="relative w-full sm:w-auto">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-space-40"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search files..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 w-full sm:w-56 text-sm border border-space-30 rounded-xl
                           focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60
                           placeholder:text-space-40 transition-all"
              />
            </div>
          )}
        </div>

        {/* Filter tabs (Claude-style pills) */}
        {showTabs && (
          <div className="flex items-center gap-1.5 mb-5">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  tab === t.key
                    ? "bg-primary text-on-primary"
                    : "text-space-50 hover:text-space hover:bg-space-20"
                }`}
              >
                {t.label}
                {(t.count ?? 0) > 0 && <span className="ml-1.5 opacity-70">{t.count}</span>}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {visibleCount === 0 ? (
          q ? (
            <div className="text-center py-16">
              <p className="text-sm text-space-40">No artifacts match "{search}"</p>
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-14 h-14 rounded-2xl bg-jove-30 flex items-center justify-center mx-auto mb-4 animate-[float_3s_ease-in-out_infinite]">
                <svg className="w-7 h-7 text-jove-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-space-50">
                {tab === "shared"
                  ? "Nothing shared with you yet"
                  : tab === "byyou"
                    ? "You haven't shared anything yet"
                    : tab === "exposure"
                      ? "No live public links"
                      : "No artifacts yet"}
              </p>
              <p className="text-xs text-space-40 mt-1">
                {tab === "shared"
                  ? "Links teammates share with the org will appear here."
                  : tab === "byyou"
                    ? "Use Share on a file in any conversation to create an org link."
                    : tab === "exposure"
                      ? "Nothing is publicly reachable right now."
                      : "Start a conversation to generate your first file."}
              </p>
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {showYours && filteredArtifacts.map((item) => {
              const icon = FILE_ICONS[item.file_type] ?? "📎";
              const ext = item.file_type.toUpperCase();
              return (
                <div
                  key={item.id}
                  className="group rounded-xl border border-space-30 bg-surface hover:border-jove-60/40 hover:shadow-sm transition-all"
                >
                  <button
                    onClick={() => openArtifactSmart(item.id, item.filename)}
                    className="w-full text-left px-4 py-3.5 rounded-t-xl hover:bg-jove-10/30 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-jove-30 flex items-center justify-center shrink-0 group-hover:bg-jove-50 transition-colors">
                        <span className="text-lg">{icon}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-space truncate">{item.filename}</p>
                        <p className="text-[11px] text-space-40 mt-0.5 truncate">
                          {item.conversation_title || "Untitled conversation"}
                        </p>
                        <p className="text-[10px] text-space-40 mt-1">
                          {ext} &middot; {formatDate(item.created_at)}
                          {myShared.has(item.id) && (
                            <span className="ml-1.5 inline-block px-1.5 py-px rounded-full bg-jove-30 text-jove-80 font-semibold">
                              Shared
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                  <div className="flex border-t border-space-30/50">
                    <button
                      onClick={() => openArtifactSmart(item.id, item.filename)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-space-40
                                 hover:text-jove hover:bg-jove-10/30 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                      Open
                    </button>
                    {showReveal && (
                      <>
                        <div className="w-px bg-space-30/50" />
                        <button
                          onClick={() => revealArtifact(item.id)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-space-40
                                     hover:text-jove hover:bg-jove-10/30 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                          </svg>
                          Folder
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {showByYou && filteredByYou.map((s) => {
              const icon = FILE_ICONS[s.file_type] ?? "📎";
              return (
                <div
                  key={s.token}
                  className="group rounded-xl border border-space-30 bg-surface hover:border-jove-60/40 hover:shadow-sm transition-all"
                >
                  <button
                    onClick={() => window.open(s.url, "_blank", "noopener,noreferrer")}
                    className="w-full text-left px-4 py-3.5 rounded-t-xl hover:bg-jove-10/30 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-jove-30 flex items-center justify-center shrink-0 group-hover:bg-jove-50 transition-colors">
                        <span className="text-lg">{icon}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-space truncate">{s.filename}</p>
                        <p className="text-[11px] text-space-40 mt-0.5 truncate">
                          {s.scope === "public"
                            ? `Public link${s.expires_at ? ` · expires ${formatDate(s.expires_at)}` : ""}`
                            : s.scope === "people"
                              ? `Shared with ${(s.allowed_user_ids ?? []).length} ${(s.allowed_user_ids ?? []).length === 1 ? "person" : "people"}`
                              : "Shared with the org"}
                        </p>
                        <p className="text-[10px] text-space-40 mt-1 flex items-center gap-1">
                          {s.file_type.toUpperCase()} &middot; {formatDate(s.created_at)}
                          <span className="inline-flex items-center gap-0.5">
                            &middot;
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {s.views}
                          </span>
                        </p>
                      </div>
                    </div>
                  </button>
                  <div className="flex border-t border-space-30/50">
                    <button
                      onClick={() => copyLink(s.url)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-space-40
                                 hover:text-jove hover:bg-jove-10/30 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                      </svg>
                      Copy link
                    </button>
                    <div className="w-px bg-space-30/50" />
                    <button
                      onClick={() => revoke(s.token)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-space-40
                                 hover:text-hydra hover:bg-hydra-50/30 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      Revoke
                    </button>
                  </div>
                </div>
              );
            })}

            {showExposure && filteredExposure.map((s) => {
              const icon = FILE_ICONS[s.file_type] ?? "📎";
              return (
                <div
                  key={s.token}
                  className="group rounded-xl border border-space-30 bg-surface hover:border-jove-60/40 hover:shadow-sm transition-all"
                >
                  <button
                    onClick={() => window.open(s.url, "_blank", "noopener,noreferrer")}
                    className="w-full text-left px-4 py-3.5 rounded-t-xl hover:bg-jove-10/30 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-jove-30 flex items-center justify-center shrink-0 group-hover:bg-jove-50 transition-colors">
                        <span className="text-lg">{icon}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-space truncate">{s.filename}</p>
                        <p className="text-[11px] text-space-40 mt-0.5 truncate">
                          Public — shared by {s.shared_by}
                          {s.expires_at ? ` · expires ${formatDate(s.expires_at)}` : ""}
                        </p>
                        <p className="text-[10px] text-space-40 mt-1">
                          {s.file_type.toUpperCase()} &middot; {formatDate(s.created_at)} &middot; {s.views} view{s.views !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  </button>
                  <div className="flex border-t border-space-30/50">
                    <button
                      onClick={() => revoke(s.token)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-space-40
                                 hover:text-hydra hover:bg-hydra-50/30 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      Revoke public link
                    </button>
                  </div>
                </div>
              );
            })}

            {showShared && filteredShares.map((s) => {
              const icon = FILE_ICONS[s.file_type] ?? "📎";
              return (
                <div
                  key={s.token}
                  className="group rounded-xl border border-space-30 bg-surface hover:border-jove-60/40 hover:shadow-sm transition-all"
                >
                  <button
                    onClick={() => window.open(s.url, "_blank", "noopener,noreferrer")}
                    className="w-full text-left px-4 py-3.5 rounded-xl hover:bg-jove-10/30 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-jove-30 flex items-center justify-center shrink-0 group-hover:bg-jove-50 transition-colors">
                        <span className="text-lg">{icon}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-space truncate">{s.filename}</p>
                        <p className="text-[11px] text-space-40 mt-0.5 truncate">
                          Shared by {s.shared_by}
                        </p>
                        <p className="text-[10px] text-space-40 mt-1 flex items-center gap-1">
                          {s.file_type.toUpperCase()} &middot; {formatDate(s.created_at)}
                          {s.views > 0 && (
                            <span className="inline-flex items-center gap-0.5">
                              &middot;
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {s.views}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
